import React, { useMemo, useState } from 'react';
import useUrlState from 'Hooks/useUrlState';
import OutstandingPaymentsTable from './Table/OutstandingPaypalPaymentsTable';
import useAbortableEffect from 'Hooks/useAbortableEffect';
import useMicrowavePayments from '../../useMicrowavePayments';
import styles from '../OutstandingPayments.module.css';
import { Skeleton } from '@round/ui-kit';
import { OutstandingPaymentsSortingKey, OutstandingPaymentsTableRow } from '../helpers';
import OutstandingPaymentsFilters, { getFilterValues } from '../OutstandingPaymentsFilters/OutstandingPaymentsFilters';
import { microwave } from '@round/api';
import PaypalPaymentModal from '../../PaymentModals/PaypalPaymentsModal/PaypalPaymentsModal';
import { RowSelectionState } from '@tanstack/react-table';
import uniqBy from 'lodash/uniqBy';
import { ReactComponent as PayPalIcon } from 'assets/PayPalShort.svg';
import Button from 'ui/Button/Button';
import Header from 'ui-new/Layout/Header/Header';

type UrlState = microwave.GetPaymentRequestsParams &
    Partial<{
        filters: string;
    }>;

const initialUrlState: Pick<Required<UrlState>, 'page' | 'page_size'> = {
    page: 1,
    page_size: 10,
};

const OutstandingPaypalPayments = () => {
    const [urlState, setUrlState] = useUrlState<UrlState>(initialUrlState);
    const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
    const [selectedRow, setSelectedRow] = useState<OutstandingPaymentsTableRow | null>(null);
    const [selectedBulkPaymentRows, setSelectedBulkPaymentRows] = useState<OutstandingPaymentsTableRow[]>([]);

    const page = urlState.page ? Number(urlState.page) : initialUrlState.page;
    const pageSize = urlState.page_size ? Number(urlState.page_size) : initialUrlState.page_size;

    const ordering: OutstandingPaymentsSortingKey[] = urlState.ordering
        ? (urlState.ordering.split(',') as OutstandingPaymentsSortingKey[])
        : [];

    const filters = useMemo(() => getFilterValues(urlState), [urlState]);
    const apiFilters = useMemo(() => ({ ...filters, microwave_influencer_account_id: undefined }), [filters]);

    const { rows, totals, count, status, fetchData, updatePayment, makePaypalPayment, reset } = useMicrowavePayments();

    const isInitialized = status === 'initialized' || status === 'error';

    useAbortableEffect(
        (signal) => {
            if (!isInitialized) {
                fetchData(
                    {
                        page,
                        page_size: pageSize,
                        preferred_payment_method: 'PAYPAL',
                        payment_status: 'UNPAID',
                        status: 'APPROVED,APPROVED_HIDDEN',
                        ordering: urlState.ordering || 'payment_deadline,-release_id',
                        ...apiFilters,
                    },
                    { signal }
                );
            }
        },
        [fetchData, page, pageSize, urlState.ordering, apiFilters, isInitialized]
    );

    const handleUpdatePayment = async (id: number, data: microwave.PatchPaymentRequestBody) => {
        const response = await updatePayment(id, data);
        if (response.status === 200) {
            setSelectedBulkPaymentRows((prev) =>
                prev.map((row) => (row.id === id ? { ...row, ...response.data } : row))
            );
        }
        return response;
    };

    const rowsDisplayedText = `displaying ${rows.length} out of ${count || 0} post${count !== 1 ? 's' : ''}`;
    const paypalPaymentRows = useMemo(
        () => (selectedRow ? [selectedRow] : selectedBulkPaymentRows),
        [selectedBulkPaymentRows, selectedRow]
    );

    return (
        <div className={styles.container}>
            <Header className={styles.header}>
                <div>
                    <Header.Title>Paypal payment</Header.Title>
                    <p className={styles.rowCountText}>{!isInitialized ? <Skeleton /> : rowsDisplayedText}</p>
                </div>

                <div className={styles.optionsContainer}>
                    <OutstandingPaymentsFilters
                        value={filters}
                        onChange={(val) => {
                            setUrlState({ ...val, page: 1 });
                            reset();
                        }}
                    />

                    {selectedBulkPaymentRows.length > 0 && (
                        <>
                            <Button
                                onClick={() => setIsPaymentModalOpen(true)}
                                rightIcon={<PayPalIcon />}
                                appearance="outlined"
                            >
                                <span>Pay all {selectedBulkPaymentRows.length} Selected</span>
                            </Button>

                            <Button onClick={() => setSelectedBulkPaymentRows([])} appearance="outlined">
                                Clear
                            </Button>
                        </>
                    )}
                </div>
            </Header>

            <OutstandingPaymentsTable
                isLoading={!isInitialized}
                data={rows}
                totals={totals}
                count={count}
                page={page}
                setPage={(page) => {
                    setUrlState({ page });
                    reset();
                }}
                pageSize={pageSize}
                setPageSize={(pageSize) => {
                    setUrlState({ page_size: pageSize || initialUrlState.page_size });
                    reset();
                }}
                manualPagination
                ordering={ordering}
                onOrderingChange={(ordering) => {
                    setUrlState({
                        ordering: ordering.toString(),
                    });
                    reset();
                }}
                noDataLabel={status === 'error' ? 'Could not load payments' : 'No payments found'}
                updatePayment={handleUpdatePayment}
                onClickMakePayment={(row) => {
                    setSelectedRow(row);
                    setIsPaymentModalOpen(true);
                }}
                getRowId={(row, index) => (row.id ? `${row.id}` : `skeleton-${index}`)}
                enableRowSelection
                enableMultiRowSelection
                rowSelection={selectedBulkPaymentRows.reduce((acc, row) => {
                    acc[`${row.id}`] = true;
                    return acc;
                }, {} as RowSelectionState)}
                onRowSelectionChange={(selection) => {
                    const incomingRows = rows.filter((row) => selection[`${row.id}`]);

                    setSelectedBulkPaymentRows((prev) =>
                        uniqBy([...prev.filter((row) => selection[`${row.id}`]), ...incomingRows], 'id')
                    );
                }}
            />

            <PaypalPaymentModal
                rows={paypalPaymentRows}
                updatePayment={handleUpdatePayment}
                makePayment={makePaypalPayment}
                isOpen={isPaymentModalOpen}
                onClose={() => {
                    setIsPaymentModalOpen(false);
                    setSelectedRow(null);
                }}
                onComplete={() => {
                    setSelectedBulkPaymentRows([]);
                    reset();
                }}
            />
        </div>
    );
};

export default OutstandingPaypalPayments;
